import React, { useEffect, useContext } from 'react';

import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component';

import { useAllCommentsData } from './useAllCommentsData';
import { getCommentsDataManagerExperimentAttributes } from './getCommentsDataManagerExperimentAttributes';

// Initiates the loading of all comment data for the specified page in the background

// Experience tracking constants
const COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE = 'comments-data-background-load';

type CommentsDataBackgroundLoaderProps = {
	contentId: string;
};

export const CommentsDataBackgroundLoader = ({ contentId }: CommentsDataBackgroundLoaderProps) => {
	// We can safely use a component-scoped ref here because even if we switch to a different page,
	// the new page's instance of this component will attempt to load comments again - but since the
	// data will already be in the GQL cache, it won't actually do anything.
	const lastContentIdRef = React.useRef<string | null>(null);

	const experienceTracker = useContext(ExperienceTrackerContext);

	// Call the hook to load all comments - should only when content has settled and is ready so we
	// don't impact loading times
	const { loading, error } = useAllCommentsData({
		contentId,
		options: {
			skip: !contentId || lastContentIdRef.current === contentId,
			fetchPolicy: 'network-only',
		},
	});

	// Experience tracking: Handle success/failure when loading completes
	useEffect(() => {
		if (!contentId) {
			return;
		}

		// Only track completion if we're not still loading
		if (!loading) {
			if (error) {
				// Experience failed due to error
				experienceTracker.fail({
					name: COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE,
					error,
				});
			} else {
				// Experience succeeded - comments data loaded successfully
				experienceTracker.succeed({
					name: COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE,
				});
			}
		}
	}, [loading, error, contentId, experienceTracker]);

	useEffect(() => {
		lastContentIdRef.current = contentId;
	}, [contentId]);

	// This component only triggers the hook, doesn't render anything other than the ExperienceStart,
	// which handles being rendered multiple times with the same props gracefully
	return (
		<ExperienceStart
			name={COMMENTS_DATA_BACKGROUND_LOAD_EXPERIENCE}
			attributes={{
				...getCommentsDataManagerExperimentAttributes(),
				contentId,
			}}
		/>
	);
};
